import React, {
  useState,
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import UserMenu from "./userMenu";
import { Link } from "react-router-dom";
import {
  AlignLeft,
  MoreHorizontal,
  AlertCircle,
  Clipboard,
  MessageCircle,
  RefreshCw,
  Shield,
  Bell,
} from "react-feather";

const API_BASE_URL = "https://api.schoolpay.tech";

const Header = () => {
  const [headerbar, setHeaderbar] = useState(true);
  const [pajskSummary, setPajskSummary] = useState(null);
  const [complaintSummary, setComplaintSummary] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingDropdownOpen, setPendingDropdownOpen] = useState(false);

  const pendingRef = useRef(null);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("secure_token") ||
      ""
    );
  };

  const fetchPendingSummary = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setPendingLoading(true);

      const [pajskRes, complaintRes] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/pajsk/getPendingActionSummary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
        }),
        fetch(`${API_BASE_URL}/complaint/getComplaintSummaryAsAdmin`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
        }),
      ]);

      if (pajskRes.status === "fulfilled") {
        const pajskJson = await pajskRes.value.json();

        if (pajskJson.response_code === 1000 || pajskJson.code === 1000) {
          const data = pajskJson.data || {};

          setPajskSummary(
            data.pajsk
              ? data.pajsk
              : {
                  total_records: Number(data.total_records || 0),
                  total_pending_review: Number(
                    data.total_pending_review ||
                      data.pending_count ||
                      data.total_pending ||
                      0
                  ),
                  total_verified: Number(data.total_verified || 0),
                  total_rejected: Number(data.total_rejected || 0),
                }
          );
        } else {
          setPajskSummary(null);
        }
      } else {
        console.error("[Header] PAJSK pending failed:", pajskRes.reason);
        setPajskSummary(null);
      }

      if (complaintRes.status === "fulfilled") {
        const complaintJson = await complaintRes.value.json();

        if (
          complaintJson.response_code === 1000 ||
          complaintJson.code === 1000
        ) {
          const data = complaintJson.data || {};

          setComplaintSummary({
            total_complaints: Number(data.total_complaints || 0),
            total_pending: Number(data.total_pending || 0),
            total_in_progress: Number(data.total_in_progress || 0),
            total_resolved: Number(data.total_resolved || 0),
            total_not_responded: Number(data.total_not_responded || 0),
            total_responded: Number(data.total_responded || 0),
          });
        } else {
          console.warn("[Header] complaint summary invalid:", complaintJson);
          setComplaintSummary(null);
        }
      } else {
        console.error("[Header] complaint summary failed:", complaintRes.reason);
        setComplaintSummary(null);
      }
    } catch (error) {
      console.error("[Header] pending summary error:", error);
      setPajskSummary(null);
      setComplaintSummary(null);
    } finally {
      setPendingLoading(false);
    }
  };

  useLayoutEffect(() => {
    function updateSize() {
      const header = document.querySelector(".page-main-header");
      const sidebar = document.querySelector(".page-sidebar");

      if (!header || !sidebar) return;

      if (window.innerWidth <= 991) {
        header.classList.add("open");
        sidebar.classList.add("open");
      } else {
        header.classList.remove("open");
        sidebar.classList.remove("open");
      }
    }

    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    const header = document.querySelector(".page-main-header");
    const sidebar = document.querySelector(".page-sidebar");

    if (!header || !sidebar) return;

    if (window.innerWidth <= 991) {
      header.classList.add("open");
      sidebar.classList.add("open");
    } else {
      header.classList.remove("open");
      sidebar.classList.remove("open");
    }
  }, []);

  useEffect(() => {
    fetchPendingSummary();

    const timer = setInterval(() => {
      fetchPendingSummary();
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!pendingRef.current) return;

      if (!pendingRef.current.contains(event.target)) {
        setPendingDropdownOpen(false);
      }
    };

    if (pendingDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [pendingDropdownOpen]);

  function openCloseSidebar() {
    const header = document.querySelector(".page-main-header");
    const sidebar = document.querySelector(".page-sidebar");

    if (!header || !sidebar) return;

    if (document.querySelector(".page-sidebar.open")) {
      header.classList.remove("open");
      sidebar.classList.remove("open");
    } else {
      header.classList.add("open");
      sidebar.classList.add("open");
    }
  }

  function goFull(event) {
    event.preventDefault();

    if (
      (document.fullScreenElement && document.fullScreenElement !== null) ||
      (!document.mozFullScreen && !document.webkitIsFullScreen)
    ) {
      if (document.documentElement.requestFullScreen) {
        document.documentElement.requestFullScreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullScreen) {
        document.documentElement.webkitRequestFullScreen(
          Element.ALLOW_KEYBOARD_INPUT
        );
      }
    } else {
      if (document.cancelFullScreen) {
        document.cancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      }
    }
  }

  const pajskPending = Number(
    pajskSummary?.total_pending_review ||
      pajskSummary?.pending_count ||
      pajskSummary?.total_pending ||
      0
  );

  const complaintPending =
    Number(complaintSummary?.total_pending || 0) +
    Number(complaintSummary?.total_in_progress || 0) +
    Number(complaintSummary?.total_not_responded || 0);

  const totalPendingActions = pajskPending + complaintPending;

  return (
    <Fragment>
      <style>{`
        .page-main-header {
          overflow: visible !important;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,251,255,0.96)) !important;
          border-bottom: 1px solid #DBEAFE !important;
          box-shadow: 0 10px 30px rgba(8, 27, 58, 0.055) !important;
          backdrop-filter: blur(16px);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }

        .main-header-right,
        .nav-right,
        .nav-menus {
          overflow: visible !important;
        }

        .main-header-right {
          min-height: 68px;
          align-items: center;
        }

        .nav-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        .nav-menus {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 10px;
          flex-wrap: nowrap;
          margin: 0;
          padding: 0 16px 0 0;
        }

        .nav-menus > li {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin: 0 !important;
        }

        .nav-menus a,
        .nav-menus button {
          text-decoration: none;
        }

        .mobile-sidebar a {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #EEF6FF;
          border: 1px solid #DBEAFE;
          color: #0B4C9C;
          transition: 0.16s ease;
        }

        .mobile-sidebar a:hover {
          background: #DBEAFE;
          color: #07172F;
          transform: translateY(-1px);
        }

        .spgovMobileBrand {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          color: #07172F;
        }

        .spgovMobileMark {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          background: linear-gradient(135deg, #07172F 0%, #0B4C9C 100%);
          color: #FFFFFF;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: -0.02em;
          box-shadow: 0 10px 24px rgba(11, 76, 156, 0.18);
        }

        .spgovMobileText strong {
          display: block;
          font-size: 14px;
          font-weight: 700;
          line-height: 1;
          color: #07172F;
        }

        .spgovMobileText span {
          display: block;
          margin-top: 3px;
          font-size: 10.5px;
          font-weight: 500;
          color: #64748B;
        }

        .spgovSecurePill {
          height: 42px;
          padding: 0 13px;
          border-radius: 14px;
          border: 1px solid #DBEAFE;
          background: #FFFFFF;
          color: #0B4C9C;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 8px 20px rgba(8, 27, 58, 0.045);
          white-space: nowrap;
        }

        .pendingActionWrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          margin-right: 2px;
          z-index: 10020;
        }

        .pendingActionButton {
          height: 42px;
          border-radius: 14px;
          border: 1px solid #DBEAFE;
          background: #FFFFFF;
          color: #475569;
          padding: 0 13px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          transition: 0.16s ease;
          white-space: nowrap;
          box-shadow: 0 8px 20px rgba(8, 27, 58, 0.045);
        }

        .pendingActionButton:hover {
          transform: translateY(-1px);
          border-color: #BFDBFE;
          box-shadow: 0 12px 26px rgba(8, 27, 58, 0.08);
        }

        .pendingActionButton.hasPending {
          background: linear-gradient(180deg, #FFFFFF, #FFF7ED);
          color: #9A3412;
          border-color: #FED7AA;
          box-shadow: 0 10px 24px rgba(234, 88, 12, 0.12);
        }

        .pendingActionIcon {
          width: 17px;
          height: 17px;
          flex: 0 0 auto;
          color: #0B4C9C;
        }

        .pendingActionButton.hasPending .pendingActionIcon {
          color: #EA580C;
        }

        .pendingActionCount {
          min-width: 24px;
          height: 24px;
          border-radius: 999px;
          padding: 0 7px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #EEF6FF;
          color: #0B4C9C;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        .pendingActionButton.hasPending .pendingActionCount {
          background: #EA580C;
          color: #FFFFFF;
        }

        .pendingActionText {
          white-space: nowrap;
          line-height: 1;
        }

        .pendingActionDropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          width: min(390px, calc(100vw - 24px));
          max-width: calc(100vw - 24px);
          background: #FFFFFF;
          border: 1px solid #DBEAFE;
          border-radius: 20px;
          box-shadow: 0 24px 60px rgba(8, 27, 58, 0.18);
          padding: 12px;
          z-index: 10030;
        }

        .pendingActionDropdown::before {
          content: "";
          position: absolute;
          top: -7px;
          right: 28px;
          width: 14px;
          height: 14px;
          background: #FFFFFF;
          border-left: 1px solid #DBEAFE;
          border-top: 1px solid #DBEAFE;
          transform: rotate(45deg);
        }

        .pendingDropdownHeader {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 5px 5px 13px;
          border-bottom: 1px solid #EEF2F7;
          margin-bottom: 8px;
        }

        .pendingDropdownHeader strong {
          display: block;
          font-size: 15px;
          font-weight: 700;
          color: #07172F;
          letter-spacing: -0.01em;
        }

        .pendingDropdownHeader span {
          display: block;
          margin-top: 3px;
          font-size: 12px;
          font-weight: 400;
          color: #64748B;
        }

        .pendingDropdownHeader button {
          height: 32px;
          border: 1px solid #DBEAFE;
          background: #F8FBFF;
          border-radius: 11px;
          padding: 0 10px;
          font-size: 11.5px;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .pendingDropdownHeader button:hover {
          background: #EEF6FF;
          color: #0B4C9C;
        }

        .pendingDropdownItem {
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 12px 10px;
          border-radius: 15px;
          color: inherit;
          text-decoration: none;
          transition: 0.16s ease;
        }

        .pendingDropdownItem:hover {
          background: #F8FBFF;
          text-decoration: none;
          transform: translateY(-1px);
        }

        .pendingDropdownItem.urgent {
          background: #FFF7ED;
        }

        .pendingDropdownItem.urgent:hover {
          background: #FFEDD5;
        }

        .pendingDropdownItem.complaintUrgent {
          background: #FEF2F2;
        }

        .pendingDropdownItem.complaintUrgent:hover {
          background: #FEE2E2;
        }

        .pendingItemIcon {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          flex: 0 0 auto;
        }

        .pendingItemIcon.pajsk {
          background: #FFF7ED;
          color: #EA580C;
        }

        .pendingItemIcon.complaint {
          background: #FEF2F2;
          color: #DC2626;
        }

        .pendingItemContent {
          min-width: 0;
          flex: 1;
        }

        .pendingItemContent strong {
          display: block;
          font-size: 13.5px;
          font-weight: 650;
          color: #07172F;
        }

        .pendingItemContent span {
          display: block;
          margin-top: 3px;
          font-size: 12px;
          font-weight: 400;
          color: #64748B;
          white-space: normal;
          line-height: 1.35;
        }

        .pendingItemCount {
          min-width: 30px;
          height: 30px;
          border-radius: 999px;
          background: #EEF6FF;
          color: #0B4C9C;
          display: grid;
          place-items: center;
          font-size: 12px;
          font-weight: 700;
          flex: 0 0 auto;
        }

        .pendingDropdownItem.urgent .pendingItemCount {
          background: #EA580C;
          color: #FFFFFF;
        }

        .pendingDropdownItem.complaintUrgent .pendingItemCount {
          background: #DC2626;
          color: #FFFFFF;
        }

        .nav-menus .profile-nav,
        .nav-menus .onhover-dropdown,
        .nav-menus .user-profile,
        .nav-menus .user-menu,
        .nav-menus .profile-dropdown {
          display: inline-flex;
          align-items: center;
        }

        .nav-menus .profile-nav > a,
        .nav-menus .onhover-dropdown > a,
        .nav-menus .user-profile > a,
        .nav-menus .user-menu > a,
        .nav-menus .profile-dropdown > a {
          min-height: 42px;
          border-radius: 14px;
          border: 1px solid #DBEAFE;
          background: #FFFFFF;
          padding: 0 12px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #334155 !important;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 8px 20px rgba(8, 27, 58, 0.045);
          transition: 0.16s ease;
        }

        .nav-menus .profile-nav > a:hover,
        .nav-menus .onhover-dropdown > a:hover,
        .nav-menus .user-profile > a:hover,
        .nav-menus .user-menu > a:hover,
        .nav-menus .profile-dropdown > a:hover {
          transform: translateY(-1px);
          border-color: #BFDBFE;
          box-shadow: 0 12px 26px rgba(8, 27, 58, 0.08);
          text-decoration: none;
        }

        .nav-menus a[href*="logout"],
        .nav-menus button.logout,
        .nav-menus .logout,
        .nav-menus .logout-btn,
        .nav-menus .btn-logout {
          min-width: 104px;
          height: 38px;
          padding: 0 14px !important;
          border-radius: 12px !important;
          border: 1px solid #FECACA !important;
          background: #FEF2F2 !important;
          color: #B91C1C !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          box-shadow: none !important;
          text-align: center;
        }

        .nav-menus a[href*="logout"]:hover,
        .nav-menus button.logout:hover,
        .nav-menus .logout:hover,
        .nav-menus .logout-btn:hover,
        .nav-menus .btn-logout:hover {
          background: #FEE2E2 !important;
          color: #991B1B !important;
          text-decoration: none !important;
        }

        .nav-menus .profile-dropdown li:last-child a {
          min-width: 104px;
          height: 38px;
          padding: 0 14px !important;
          border-radius: 12px !important;
          border: 1px solid #FECACA !important;
          background: #FEF2F2 !important;
          color: #B91C1C !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          box-shadow: none !important;
          text-align: center;
        }

        .nav-menus .profile-dropdown li:last-child a:hover {
          background: #FEE2E2 !important;
          color: #991B1B !important;
          text-decoration: none !important;
        }

        @media (max-width: 1199px) {
          .pendingActionButton {
            height: 40px;
            min-width: 104px;
            padding: 0 11px;
            border-radius: 13px;
            gap: 7px;
          }

          .pendingActionText {
            display: inline-flex;
            font-size: 12px;
          }

          .pendingActionCount {
            min-width: 22px;
            height: 22px;
            font-size: 10.5px;
          }
        }

        @media (max-width: 991px) {
          .nav-menus {
            gap: 7px;
            padding-right: 8px;
          }

          .pendingActionWrapper {
            margin-right: 0;
          }

          .pendingActionButton {
            width: auto;
            min-width: 96px;
            height: 40px;
            padding: 0 10px;
            border-radius: 13px;
            position: relative;
          }

          .pendingActionIcon {
            width: 16px;
            height: 16px;
          }

          .pendingActionText {
            display: inline-flex;
            font-size: 11.5px;
            font-weight: 600;
          }

          .pendingActionCount {
            min-width: 22px;
            height: 22px;
            padding: 0 6px;
            font-size: 10.5px;
            position: static;
            box-shadow: none;
          }

          .pendingActionDropdown {
            position: fixed;
            top: 68px;
            right: 12px;
            left: 12px;
            width: auto;
            max-width: none;
            border-radius: 18px;
          }

          .pendingActionDropdown::before {
            display: none;
          }
        }

        @media (max-width: 575px) {
          .nav-menus {
            gap: 6px;
            padding-right: 6px;
          }

          .spgovMobileText,
          .spgovSecurePill {
            display: none;
          }

          .pendingActionButton {
            min-width: 88px;
            height: 38px;
            padding: 0 8px;
            gap: 5px;
            border-radius: 12px;
          }

          .pendingActionIcon {
            width: 15px;
            height: 15px;
          }

          .pendingActionText {
            display: inline-flex;
            font-size: 11px;
          }

          .pendingActionCount {
            min-width: 20px;
            height: 20px;
            padding: 0 5px;
            font-size: 10px;
          }

          .pendingDropdownHeader {
            align-items: center;
          }

          .pendingDropdownHeader span {
            display: none;
          }

          .pendingDropdownItem {
            padding: 12px 10px;
          }

          .pendingItemIcon {
            width: 36px;
            height: 36px;
          }

          .pendingItemContent strong {
            font-size: 13px;
          }

          .pendingItemContent span {
            font-size: 11px;
          }

          .pendingItemCount {
            min-width: 28px;
            height: 28px;
            font-size: 11px;
          }

          .nav-menus .profile-nav > a,
          .nav-menus .onhover-dropdown > a,
          .nav-menus .user-profile > a,
          .nav-menus .user-menu > a,
          .nav-menus .profile-dropdown > a {
            min-height: 40px;
            border-radius: 13px;
            padding: 0 10px;
          }
        }

        @media (max-width: 420px) {
          .pendingActionButton {
            min-width: 76px;
            height: 38px;
            padding: 0 7px;
          }

          .pendingActionText {
            font-size: 10.5px;
          }

          .pendingActionIcon {
            display: none;
          }
        }

        @media (max-width: 380px) {
          .pendingActionDropdown {
            top: 62px;
            right: 8px;
            left: 8px;
            padding: 10px;
          }

          .pendingDropdownHeader button span {
            display: none;
          }
        }
      `}</style>

      <div className="page-main-header">
        <div className="main-header-right row">
          <div className="mobile-sidebar d-block col-auto">
            <div className="flex-grow-1 text-end switch-sm">
              <label className="switch">
                <a
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    openCloseSidebar();
                  }}
                  aria-label="Open navigation"
                >
                  <AlignLeft size={20} />
                </a>
              </label>
            </div>
          </div>

          <div className="main-header-left d-lg-none col-auto">
            <div className="logo-wrapper">
              <Link to="/dashboard" className="spgovMobileBrand">
                <div className="spgovMobileMark">SP</div>
                <div className="spgovMobileText">
                  <strong>SPGO.my</strong>
                  <span>Government Portal</span>
                </div>
              </Link>
            </div>
          </div>

          <div className="nav-right col p-0">
            <ul className={`nav-menus ${headerbar ? "" : "open"}`}>
              <li>
                <a
                  onClick={goFull}
                  className="text-dark"
                  href="!"
                  aria-label="Toggle fullscreen"
                />
              </li>

              <li className="pendingActionWrapper" ref={pendingRef}>
                <button
                  type="button"
                  className={`pendingActionButton ${
                    totalPendingActions > 0 ? "hasPending" : ""
                  }`}
                  onClick={() => setPendingDropdownOpen((prev) => !prev)}
                  aria-label="Pending actions"
                  aria-expanded={pendingDropdownOpen}
                >
                  {totalPendingActions > 0 ? (
                    <Bell className="pendingActionIcon" />
                  ) : (
                    <AlertCircle className="pendingActionIcon" />
                  )}

                  <span className="pendingActionCount">
                    {pendingLoading ? "..." : totalPendingActions}
                  </span>

                  <span className="pendingActionText">
                    {totalPendingActions > 0 ? "Pending" : "Clear"}
                  </span>
                </button>

                {pendingDropdownOpen && (
                  <div className="pendingActionDropdown">
                    <div className="pendingDropdownHeader">
                      <div>
                        <strong>Pending Actions</strong>
                        <span>Administrative items requiring attention</span>
                      </div>

                      <button type="button" onClick={fetchPendingSummary}>
                        <RefreshCw size={12} />
                        <span>Refresh</span>
                      </button>
                    </div>

                    <Link
                      to="/pajsk"
                      className={`pendingDropdownItem ${
                        pajskPending > 0 ? "urgent" : ""
                      }`}
                      onClick={() => setPendingDropdownOpen(false)}
                    >
                      <div className="pendingItemIcon pajsk">
                        <Clipboard size={16} />
                      </div>

                      <div className="pendingItemContent">
                        <strong>Activity Records</strong>
                        <span>
                          {pajskPending > 0
                            ? `${pajskPending} records pending review`
                            : "No activity records pending"}
                        </span>
                      </div>

                      <div className="pendingItemCount">{pajskPending}</div>
                    </Link>

                    <Link
                      to="/feedback"
                      className={`pendingDropdownItem ${
                        complaintPending > 0 ? "complaintUrgent" : ""
                      }`}
                      onClick={() => setPendingDropdownOpen(false)}
                    >
                      <div className="pendingItemIcon complaint">
                        <MessageCircle size={16} />
                      </div>

                      <div className="pendingItemContent">
                        <strong>Public Feedback</strong>
                        <span>
                          {complaintPending > 0
                            ? `${complaintPending} feedback items require action`
                            : "No feedback items pending"}
                        </span>
                      </div>

                      <div className="pendingItemCount">
                        {complaintPending}
                      </div>
                    </Link>
                  </div>
                )}
              </li>

              <li className="d-none d-md-inline-flex">
                <div className="spgovSecurePill">
                  <Shield size={15} />
                  Secure Portal
                </div>
              </li>

              <UserMenu />
            </ul>

            <div
              className="d-lg-none mobile-toggle pull-right"
              onClick={() => setHeaderbar(!headerbar)}
              role="button"
              tabIndex={0}
            >
              <MoreHorizontal />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Header;